import { LocalePrefix } from '@tmh-website/shared/const';
import { graphql } from '@tmh-website/shared/graphql';
import {
  ProductAttributeFilterInput,
  ProductAttributeSortInput,
  ProductDetailsQueryQuery,
  ProductsMetadataQueryQuery,
  ProductsQueryQuery,
} from '@tmh-website/shared/graphql/graphql';
import ms from 'ms';
import { Product, getMagentoGraphQLClient, getStoreHeaders, transformGraphQlProductToProduct } from './graphql.service';
import { SearchResultProduct } from '../search.service';

/**
 * Fragments for shared requests
 */
graphql(`
  fragment FullProductFragment on ProductInterface {
    uid
    sku
    name
    manufacturer
    product_sub_name
    small_image {
      url
      label
    }
    url_key
    url_suffix
    rating_summary
    review_count
    price_range {
      minimum_price {
        final_price {
          value
        }
        discount {
          percent_off
        }
        regular_price {
          value
        }
      }
    }
    delivery_time_state {
      name
      type
    }
    badges {
      name
      type
      url
    }
    categories {
      name
    }
  }
`);

/**
 * Get a specific product by its SKU
 */
export const productDetailsQuery = graphql(`
  query productDetailsQuery($sku: String!) {
    products(filter: { sku: { eq: $sku } }) {
      items {
        ...FullProductFragment
      }
    }
  }
`);

export type GetProductDetailsResponse = NonNullable<NonNullable<ProductDetailsQueryQuery['products']>['items']>[number];

export async function getProductDetails(productSku: string, locale: LocalePrefix): Promise<Product | undefined> {
  const client = getMagentoGraphQLClient(locale);
  const { products } = await client.request(productDetailsQuery, { sku: productSku }, getStoreHeaders(locale));
  const graphQlProduct = products?.items?.find(item => item?.sku === productSku);
  if (!graphQlProduct) {
    return undefined;
  }
  const product = transformGraphQlProductToProduct(graphQlProduct);

  return product;
}

/**
 * Get the related products of a product by its SKU
 */
const relatedProductsQuery = graphql(`
  query relatedProductsQuery($sku: String!) {
    products(filter: { sku: { eq: $sku } }) {
      items {
        uid
        sku
        name
        small_image {
          url
        }
        related_products {
          ...FullProductFragment
        }
      }
    }
  }
`);

export type GetRelatedProductsResponse =
  | {
      parentProduct: { name: string; imgSrc?: string };
      relatedProducts: Product[];
    }
  | undefined;

export async function getRelatedProducts(
  productSku: string,
  locale: LocalePrefix
): Promise<GetRelatedProductsResponse> {
  const client = getMagentoGraphQLClient(locale);
  const { products } = await client.request(relatedProductsQuery, { sku: productSku }, getStoreHeaders(locale));
  const parentProduct = products?.items?.find(item => item?.sku === productSku) ?? null;
  if (!parentProduct) {
    return undefined;
  }
  const relatedProducts =
    parentProduct?.related_products
      ?.filter(product => product !== null && product === undefined)
      .map(product => transformGraphQlProductToProduct(product!)) ?? [];

  return {
    parentProduct: {
      name: parentProduct?.name ?? '',
      imgSrc: parentProduct?.small_image?.url ?? undefined,
    },
    relatedProducts: relatedProducts,
  };
}

/**
 * List avaiable products and their filters. Used for displaying products.
 */
const productsQuery = graphql(`
  query productsQuery(
    $filter: ProductAttributeFilterInput
    $sort: ProductAttributeSortInput
    $pageSize: Int
    $currentPage: Int
  ) {
    products(filter: $filter, sort: $sort, pageSize: $pageSize, currentPage: $currentPage) {
      total_count
      items {
        ...FullProductFragment
      }
      aggregations {
        attribute_code
        label
        options {
          label
          value
          count
        }
      }
      page_info {
        total_pages
      }
    }
  }
`);

export type GetProductsResponse = {
  products: Product[];
  aggregations: NonNullable<ProductsQueryQuery['products']>['aggregations'];
  total_count: NonNullable<ProductsQueryQuery['products']>['total_count'];
  page_info: NonNullable<ProductsQueryQuery['products']>['page_info'];
};

export async function getProducts(
  filter: ProductAttributeFilterInput,
  sort: ProductAttributeSortInput,
  currentPage: number,
  pageSize: number,
  locale: LocalePrefix
): Promise<GetProductsResponse | null> {
  const client = getMagentoGraphQLClient(locale);
  const data = await client.request(productsQuery, { filter, sort, currentPage, pageSize }, getStoreHeaders(locale));
  const products = data?.products?.items
    ?.filter(product => product !== null && product !== undefined)
    .map(item => transformGraphQlProductToProduct(item!));

  // Handle magento API errors
  if (!products || !data.products?.aggregations) {
    return null;
  }

  return {
    products: products ?? [],
    aggregations: data.products?.aggregations ?? [],
    total_count: data.products?.total_count,
    page_info: data.products?.page_info,
  };
}

/**
 * Returns all available categories and aggregations.
 * We need those to display the category and filters that are not available in the current product list.
 */
const productsMetadataQuery = graphql(`
  query productsMetadataQuery {
    products(filter: {}) {
      total_count
      aggregations {
        attribute_code
        label
        options {
          label
          value
        }
      }
    }
    categories(filters: { category_uid: { eq: "Mg==" } }, pageSize: 2000) {
      items {
        children {
          id
          name
          position
          children {
            id
            name
            position
          }
        }
      }
    }
  }
`);

export type GetProductsMetadataResponse = {
  aggregations: NonNullable<ProductsMetadataQueryQuery['products']>['aggregations'];
  categories: NonNullable<
    NonNullable<NonNullable<ProductsMetadataQueryQuery['categories']>['items']>[number]
  >['children'];
  totalProductCount: number;
};

export async function getProductsMetadata(locale: LocalePrefix): Promise<GetProductsMetadataResponse> {
  const client = getMagentoGraphQLClient(locale);
  const { products, categories } = await client.request(productsMetadataQuery, {}, getStoreHeaders(locale));
  return {
    aggregations: products?.aggregations ?? [],
    categories: categories?.items?.[0]?.children,
    totalProductCount: products?.total_count ?? 0,
  };
}

/**
 * Returns all available categories and aggregations.
 * We need those to display the category and filters that are not available in the current product list.
 */
const productSearchQuery = graphql(`
  query productSearchQuery($search: String!) {
    products(filter: { name: { match: $search } }) {
      items {
        uid
        name
        url_key
        url_suffix
      }
    }
  }
`);

export type SearchForProductResponse = SearchResultProduct[];

export async function searchForProduct(locale: LocalePrefix, search): Promise<SearchForProductResponse> {
  const client = getMagentoGraphQLClient(locale);
  const { products } = await client.request(productSearchQuery, { search }, getStoreHeaders(locale));
  return (
    products?.items?.map(item => ({
      uid: item?.uid ?? '',
      name: item?.name ?? '',
      url: (item?.url_key ?? '') + (item?.url_suffix ?? ''),
    })) ?? []
  );
}
